import {
  FileInput,
  PaletteIcon,
  PointerIcon,
  SparklesIcon,
  BarChart2Icon,
  BadgeCheckIcon,
  CloudUploadIcon,
  PhoneCallIcon,
  MailOpenIcon,
  LanguagesIcon,
  ShapesIcon,
  CirclePlayIcon,
  MousePointerClick,
  BoxesIcon,
  TextIcon,
  SwatchBookIcon,
  OrigamiIcon,
} from 'lucide-react';
import { z } from 'zod';
import { json } from '@remix-run/node';
import type { MetaFunction, ActionFunctionArgs } from '@remix-run/node';

import gen1 from '@admin/assets/gen-1.avif';
import hydraLogo from '@admin/assets/hydra_logo_schrift_schwarz.svg';
import { Form, useActionData } from '@remix-run/react';
import { useDeployEnv } from '@admin/components/utils';
import Marquee from '@admin/components/ui/marquee';
import Pricing from '@admin/components/Pricing';

export const meta: MetaFunction = () => [
  {
    title: 'Disco AI Content Studio',
  },
];

const contactFormSchema = z.object({
  'first-name': z.string().min(1, { message: 'First name is required' }),
  'last-name': z.string().min(1, { message: 'Last name is required' }),
  email: z.string().email({ message: 'Invalid email address' }),
  company: z.string().optional(),
  phone: z.string().optional(),
  message: z.string().max(500, { message: 'Message must be less than 500 characters' }),
  budget: z.enum(['under_25k', '25k-50k', '50k-100k', 'over_100k']).optional(),
});

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();

  try {
    const validatedData = contactFormSchema.parse({
      'first-name': formData.get('first-name'),
      'last-name': formData.get('last-name'),
      email: formData.get('email'),
      company: formData.get('company'),
      phone: formData.get('phone'),
      message: formData.get('message'),
      budget: formData.get('budget'),
    });

    // send slack webhook to https://hooks.slack.com/services/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
    await fetch(
      'https://hooks.slack.com/services/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',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: `New contact form submission from ${validatedData.email}`,
          blocks: [
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*First Name:* ${validatedData['first-name']}`,
              },
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*Last Name:* ${validatedData['last-name']}`,
              },
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*Email:* ${validatedData.email}`,
              },
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*Company:* ${validatedData.company || 'N/A'}`,
              },
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*Phone:* ${validatedData.phone || 'N/A'}`,
              },
            },
            {
              type: 'section',
              text: {
                type: 'mrkdwn',
                text: `*Message:* ${validatedData.message}`,
              },
            },
          ],
        }),
      },
    );

    return json({ success: true });
  } catch (error) {
    if (error instanceof z.ZodError) {
      return json(
        {
          success: false,
          errors: error.flatten().fieldErrors,
        },
        { status: 400 },
      );
    }

    // Handle other potential errors (e.g., email sending failure)
    console.error('Error submitting contact form:', error);
    return json(
      { success: false, message: 'An unexpected error occurred.' },
      { status: 500 },
    );
  }
};

// MARK: Main Component
export default function Marketing() {
  const deployEnv = useDeployEnv();
  const scrollToContact = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
    console.log(document.getElementById('contact-section'));
  };

  const scrollToFeatures = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div>
      <div className="relative isolate overflow-hidden bg-white">
        <svg
          aria-hidden="true"
          className="absolute inset-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
        >
          <defs>
            <pattern
              x="50%"
              y={-1}
              id="0787a7c5-978c-4f66-83c7-11c213f99cb7"
              width={200}
              height={200}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <rect
            fill="url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)"
            width="100%"
            height="100%"
            strokeWidth={0}
          />
        </svg>
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-24 sm:pb-32 lg:flex lg:px-8 lg:py-40">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8">
            <a
              href="https://hydra-newmedia.com"
              className="mt-6 inline-flex items-center gap-5 "
            >
              <img alt="by hydra newmedia GmbH" src={hydraLogo} className="h-8" />
            </a>
            {/* <img
            alt="Your Company"
            src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
            className="h-11"
          />
          <div className="mt-24 sm:mt-32 lg:mt-16">
            <a href="#" className="inline-flex space-x-6">
              <span className="rounded-full bg-indigo-600/10 px-3 py-1 text-sm font-semibold leading-6 text-indigo-600 ring-1 ring-inset ring-indigo-600/10">
                What's new
              </span>
              <span className="inline-flex items-center space-x-2 text-sm font-medium leading-6 text-gray-600">
                <span>Just shipped v1.0</span>
                <ChevronRightIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
              </span>
            </a>
          </div> */}
            <h1 className="mt-8 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Create inspiring E&#8209;Learnings courses in a flash using AI!
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              The new AI Content Studio empowers you to create interactive, web-based
              training on any topic you need. Quick & easy: No expert skills required!
            </p>
            <div className="mt-10 flex items-center gap-x-6">
              <a
                href="#features-section"
                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={scrollToFeatures}
              >
                View the benefits
              </a>
              <a
                href="#sales-section"
                className="text-sm font-semibold leading-6 text-gray-900"
                onClick={scrollToContact}
              >
                Contact us <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
          <div className="mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
            <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
              <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
                <img
                  alt="Demo E-Learning course"
                  src="https://storage.googleapis.com/hy-general-storage/disco/screenshot-hero.jpg"
                  width={2432}
                  height={1442}
                  className="w-[76rem] rounded-md shadow-2xl ring-1 ring-gray-900/10"
                />
              </div>
            </div>
          </div>
        </div>
        <Features />
        <EditorFeatures />
        <ComponentLibrary />
        <LMSFeatures />
        <Pricing />
        <div id="contact">
          <Contact />
        </div>
      </div>
    </div>
  );
}

// MARK: Feature Component
const features = [
  {
    name: 'Create courses from existing materials',
    description:
      'Let AI Content Studio automatically create dynamic e-learning courses from your existing materials, such as PDFs, videos, and documents. High data protection standards ensure your valuable content remains safe.',
    icon: FileInput,
  },
  {
    name: 'Create courses from scratch',
    description:
      'Just tell AI Content Studio your requirements for your desired course like topic, length and learner level. Within minutes, the powerful AI engine will find and structure the required content for you including interactive elements and engaging assessments. ',
    icon: SparklesIcon,
  },

  {
    name: 'Use engaging interactions',
    description:
      'AI Content Studio offers a suite of interactive components that keep learners engaged and motivated throughout their training, going beyond traditional SCORM.',
    icon: PointerIcon,
  },
];

function Features() {
  return (
    <div className="bg-white py-24 sm:py-32" id="ai-gen">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl sm:text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            Effortless Course Creation
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            The Authoring Tool You've Been Waiting for
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            AI Content Studio dramatically reduces e-learning course production time and
            costs, whether you're starting from scratch or using existing materials.
          </p>
        </div>
      </div>
      <div className="relative overflow-hidden pt-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <img
            alt="App screenshot"
            src={gen1}
            width={2432}
            height={1442}
            className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-gray-900/10"
          />
          <div aria-hidden="true" className="relative">
            <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-white pt-[7%]" />
          </div>
        </div>
      </div>
      <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
        <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base leading-7 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
          {features.map(feature => (
            <div key={feature.name} className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <feature.icon
                  aria-hidden="true"
                  className="absolute left-1 top-1 h-5 w-5 text-indigo-600"
                />
                {feature.name}
              </dt>{' '}
              <dd className="inline">{feature.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

const editorFeatures = [
  {
    name: 'Edit what the AI generates',
    description:
      'Correct mistakes and improve the quality of the generated content in record time.',
    icon: PaletteIcon,
  },
  {
    name: 'Wide range of components',
    description:
      'Mix and match interactive components, videos and media to create engaging courses.',
    icon: ShapesIcon,
  },
  {
    name: 'Make the design fit your brand',
    description:
      'Match the look and feel of your courses to your brand, ensuring a consistent and professional learner experience.',
    icon: SwatchBookIcon,
  },
  {
    name: 'Text optimization',
    description: 'Have your text checked for errors and get optimization suggestions.',
    icon: TextIcon,
  },
  {
    name: 'Automated translation',
    description:
      'Automatically translate the whole course into different languages. You can also translate individual components.',
    icon: LanguagesIcon,
  },
  {
    name: 'More custom needs?',
    description:
      'We are open to all your your customization requests. Contact us to learn more.',
    icon: OrigamiIcon,
  },
];

export function EditorFeatures() {
  return (
    <div className="overflow-hidden bg-white py-24 sm:py-32" id="e-learning-editor">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          <div className="lg:ml-auto lg:pl-4 lg:pt-4">
            <div className="lg:max-w-lg">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">
                Powerful Course Editor
              </h2>
              <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                Customization Without Limits
              </p>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Our built-in editor is the perfect tool for creating and customizing
                interactive e-learning courses. It is easy to use and provides a multitude
                of features to create enhanced learning experiences.
              </p>
              <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 text-gray-600 lg:max-w-none">
                {editorFeatures.map(feature => (
                  <div key={feature.name} className="relative pl-9">
                    <dt className="inline font-semibold text-gray-900">
                      <feature.icon
                        aria-hidden="true"
                        className="absolute left-1 top-1 h-5 w-5 text-indigo-600"
                      />
                      {feature.name}
                    </dt>{' '}
                    <dd className="inline">{feature.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <div className="flex items-start justify-end lg:order-first">
            <img
              alt="Demonstration of the editor"
              src="https://storage.googleapis.com/hy-general-storage/disco/Admin%20Sidebar.png"
              width={2432}
              height={1442}
              className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-[57rem]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// MARK: Component Library
const componentFeatures = [
  {
    name: 'Interactive Components',
    description:
      'Use quizzes, flipcards and other interactive components to engage learners. You can also require learners to complete specific tasks or provide specific information to progress through the learning experience.',
    icon: MousePointerClick,
  },
  {
    name: 'Rich Media',
    description:
      'Add videos, images, audio, and more to bring your educational vision to life.',
    icon: CirclePlayIcon,
  },

  {
    name: 'eLearning Awards',
    description:
      'Courses based on these components have won multiple awards from the eLearning Journal.',
    icon: BoxesIcon,
  },
];

function ComponentLibrary() {
  const imgUrls = [
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%931.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%931.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%931.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%932.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%932.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%932.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%933.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%933.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%933.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%934.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%934.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%934.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%935.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%935.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%935.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%936.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%936.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%936.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%937.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%937.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%937.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%938.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%938.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%938.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%939.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%939.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%939.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9310.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/MB_Krisenmanagement_Screenshot%E2%80%9310.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/NWB_Arbeitsschutz_Screenshot%E2%80%9310.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9311.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9312.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9313.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9314.jpg',
    'https://storage.googleapis.com/hy-general-storage/disco/HB_Diversity_Inclusion_Screenshot%E2%80%9315.jpg',
  ];

  const firstHalf = imgUrls.slice(0, imgUrls.length / 2);
  const secondHalf = imgUrls.slice(imgUrls.length / 2, imgUrls.length);

  return (
    <div className="bg-white py-24 sm:py-32" id="component-library">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl sm:text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            Engaging Suite of Components
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Create Courses With Award-Winning Components
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Our powerful suite of components lets you craft captivating learning
            experiences, tailored perfectly to your needs. Mix and match to create the
            ideal learning environment and watch engagement soar!
          </p>
        </div>
      </div>
      <div className="relative overflow-hidden pt-16">
        <Marquee pauseOnHover className="[--duration:110s]">
          {firstHalf.map((src, i) => (
            <div
              key={i}
              className="aspect-video h-60 overflow-clip rounded-md border shadow"
            >
              <img loading="lazy" src={src} alt="" />
            </div>
          ))}
        </Marquee>
        <Marquee reverse pauseOnHover className="[--duration:110s]">
          {secondHalf.map((src, i) => (
            <div
              key={i}
              className="aspect-video h-60 overflow-clip rounded-md border shadow"
            >
              <img loading="lazy" src={src} alt="" />
            </div>
          ))}
        </Marquee>
      </div>
      <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
        <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base leading-7 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
          {componentFeatures.map(feature => (
            <div key={feature.name} className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <feature.icon
                  aria-hidden="true"
                  className="absolute left-1 top-1 h-5 w-5 text-indigo-600"
                />
                {feature.name}
              </dt>{' '}
              <dd className="inline">{feature.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

// MARK: LMS Features
const lmsFeatures = [
  {
    name: 'Effortless Certification',
    description:
      'Recognize learner achievements with auto-generated certificates, featuring verifiable QR codes for authenticity.',
    href: '#',
    icon: BadgeCheckIcon,
  },
  {
    name: 'Flexible File Management',
    description:
      'Easily store and categorize images, videos, documents, and other resources for use in your different courses with our flexible file upload system.',
    href: '#',
    icon: CloudUploadIcon,
  },
  {
    name: 'Track Progress & Performance',
    description:
      'Gain valuable insights into learner progress and performance with integrated analytics. Optimize content and measure training impact.',
    icon: BarChart2Icon,
  },
];

function LMSFeatures() {
  return (
    <div className="bg-white py-24 sm:py-32" id="lms">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            Everything in One Place
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            AI Content Studio Comes With an LMS on Top
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            AI Content Studio includes Disco, a complete Learning Management System.
            Create courses, manage users, and track progress - all in one intuitive
            interface.
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {lmsFeatures.map(feature => (
              <div key={feature.name} className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  <feature.icon
                    aria-hidden="true"
                    className="h-5 w-5 flex-none text-indigo-600"
                  />
                  {feature.name}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                  <p className="mt-6">
                    {/* <a
                      href={feature.href}
                      className="text-sm font-semibold leading-6 text-indigo-600"
                    >
                      Learn more <span aria-hidden="true">→</span>
                    </a> */}
                  </p>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

function EnterprisePricing() {
  const scrollToContact = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white py-24 sm:py-32" id="pricing">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold leading-7 text-indigo-600">Pricing</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Pricing Plans for Teams of&nbsp;All&nbsp;Sizes.
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
          Contact Our Sales Team for a Personalized Quote.
        </p>
        <div className="mx-auto mt-8 flex max-w-2xl items-center justify-center rounded-lg bg-gray-100 p-12">
          <a
            href="mailto:pomereinke@hydra-newmedia.com"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-5 py-3 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={scrollToContact}
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
}

// MARK: Contact
function Contact() {
  const data = useActionData<typeof action>();

  if (data?.success) {
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">
              Thank you for your message!
            </h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              We'll get back to you soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white" id="contact-section">
      <div className="lg:absolute lg:inset-0 lg:left-1/2">
        <img
          alt=""
          src="https://storage.googleapis.com/hy-general-storage/disco/_mg_8230.jpg"
          className="h-64 w-full rounded-tl-xl bg-gray-50 object-cover shadow-lg sm:h-80 lg:absolute lg:h-full"
        />
      </div>
      <div className="pb-24 pt-16 sm:pb-32 sm:pt-24 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-2 lg:pt-32">
        <div className="px-6 lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Get in Touch!
            </h2>
            <p className="mt-2 text-lg leading-8 text-gray-600">
              Interested in a demo, pricing, or custom integrations? Contact us to learn
              more about how AI Content Studio can fit your organization's unique
              requirements.
            </p>
            <Form method="POST" className="mt-16">
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="first-name"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    First name
                  </label>
                  <div className="mt-2.5">
                    <input
                      id="first-name"
                      name="first-name"
                      type="text"
                      autoComplete="given-name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="last-name"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Last name
                  </label>
                  <div className="mt-2.5">
                    <input
                      id="last-name"
                      name="last-name"
                      type="text"
                      autoComplete="family-name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Email
                  </label>
                  <div className="mt-2.5">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="company"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Company
                  </label>
                  <div className="mt-2.5">
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex justify-between text-sm leading-6">
                    <label htmlFor="phone" className="block font-semibold text-gray-900">
                      Phone
                    </label>
                    <p id="phone-description" className="text-gray-400">
                      Optional
                    </p>
                  </div>
                  <div className="mt-2.5">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      aria-describedby="phone-description"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex justify-between text-sm leading-6">
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold leading-6 text-gray-900"
                    >
                      How can we help you?
                    </label>
                    <p id="message-description" className="text-gray-400">
                      Max 500 characters
                    </p>
                  </div>
                  <div className="mt-2.5">
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      aria-describedby="message-description"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      defaultValue={''}
                      maxLength={500}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-10 flex justify-end border-t border-gray-900/10 pt-8">
                <button
                  type="submit"
                  className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Send message
                </button>
              </div>
            </Form>
            <h3 className="my-6 mt-10 text-center text-lg font-semibold text-gray-600 lg:my-12">
              Or call us directly
            </h3>
            <div className="flex overflow-hidden rounded-lg border border-gray-200">
              <img
                className="block h-44 w-40 object-cover object-top"
                src="https://teamasaservice.jetzt/static/Frank_portrait-0273e748ecbb96f15814af0433fe1882.jpg"
                alt="Portrait of Frank Pomereinke"
              />
              <div className="flex flex-1 flex-col justify-center gap-2 p-1 px-5">
                <div className="text-lg font-semibold">Frank Pomereinke</div>
                <a
                  href="tel:+4915114966732"
                  className="flex items-center gap-3 rounded-lg p-1 text-indigo-500 transition-colors hover:bg-indigo-50 md:text-2xl"
                >
                  <PhoneCallIcon className="w-5" />
                  +49 0151 1496 6732
                </a>
                <a
                  href="mailto:pomereinke@hydra-newmedia.com"
                  className="flex items-center gap-3 rounded-lg p-1 text-indigo-500 transition-colors hover:bg-indigo-50"
                >
                  <MailOpenIcon className="h-5 w-5 flex-shrink-0" />
                  pomereinke@hydra-newmedia.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
